import { AddResumeBottomSheet } from "./AddResumeBottomSheet";
import { Alert, Box, Button, Flex, Heading, Text } from "../../../../../../../mads";
import { CheckCircle, Upload } from "../../../../../../../mads/icons";
import { useI18n } from "../../../../../../../shared/I18n";
import { notifyProfileDataChanged } from "../../../../../../../shared/lib/ResumeGlowup";
import { logNonFatal } from "../../../../../../../shared/native-modules/tare/logNonFatal";
import openResumeBuilderModal from "../../../../../../../shared/native-modules/tare/openResumeBuilderModal";
import { useIsDesktopDisplay } from "../../../../../context/ViewJobDisplayContext";
import { useMatchOverviewResumeUpload } from "../../../../../hooks/useMatchOverviewResumeUpload";
import { emitViewjobResumeEvent } from "../../../../../util/viewjobResumeEvents";
import { useLogging } from "../../context/useLogging";
import {
  MatchQualification,
  MatchQualificationVariant,
} from "../MatchOverviewQualificationCollection/MatchQualification";

import { borderRadius, colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useCallback, useEffect, useState } from "react";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Box's `sx` opacity does not apply reliably on web; View is required here.
import { Platform, StyleSheet, View } from "react-native";

const RESUME_BUILDER_FROM = Platform.select({
  ios: "rnviewjob-gated-match-overview-ios",
  android: "rnviewjob-gated-match-overview-android",
  default: "rnviewjob-gated-match-overview",
});

// Static placeholder rows for the greyed background list.
// Uses MatchQualification directly so the visual matches the real component.
const PLACEHOLDER_ROWS: Array<{ label: string; required: boolean }> = [
  { label: "Experience", required: true },
  { label: "Education", required: false },
  { label: "Skills", required: true },
  { label: "Certifications", required: false },
];

const noop = () => {};

/**
 * A single benefit bullet item inside the CTA card.
 */
const BenefitRow: React.FC<{ label: string }> = ({ label }) => (
  <Flex sx={styles.benefitRow}>
    <Box sx={styles.benefitIcon}>
      <CheckCircle color={colors.foregroundStatusSuccess} size="xs" />
    </Box>
    <Text level={2}>{label}</Text>
  </Flex>
);

/**
 * Disabled-state variant of the Match Overview section.
 *
 * Renders the Match Overview heading with a greyed-out, non-interactive placeholder
 * requirements list in the background and a compact CTA card overlaid on top,
 * prompting the job seeker to add a resume.
 */
export const GatedMatchOverview: React.FC = () => {
  const gt = useI18n();
  const logger = useLogging();
  const isDesktop = useIsDesktopDisplay();
  const [isAddResumeSheetVisible, setIsAddResumeSheetVisible] = useState(false);
  const {
    triggerUpload,
    loading: uploading,
    error: uploadError,
    clearError: clearUploadError,
    uploadResult,
    clearUploadResult,
  } = useMatchOverviewResumeUpload();

  useEffect(() => {
    logger.logShowedGatedMatchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuildPress = useCallback(() => {
    logger.logGatedMatchOverviewBuildTap();
    void openResumeBuilderModal(true, RESUME_BUILDER_FROM)
      .then((result) => {
        if (result === "COMPLETED") {
          logger.logGatedMatchOverviewBuildCompleted();
          emitViewjobResumeEvent("resumeBuildCompleted");
        } else if (result === "ERROR") {
          void logNonFatal(
            "openResumeBuilderModal returned ERROR",
            0,
            "viewjob.gatedMatchOverview.openResumeBuilderModalError",
            {},
          );
        }
      })
      .catch((err: unknown) => {
        void logNonFatal(
          "openResumeBuilderModal failed",
          0,
          "viewjob.gatedMatchOverview.openResumeBuilderModalError",
          { error: err instanceof Error ? err.message : String(err) },
        );
      });
  }, [logger]);

  useEffect(() => {
    if (!uploadResult) {
      return;
    }

    logger.logGatedMatchOverviewUploadCompleted(true);
    notifyProfileDataChanged();
    clearUploadResult();
  }, [uploadResult, logger, clearUploadResult]);

  useEffect(() => {
    if (!uploadError) {
      return;
    }

    logger.logGatedMatchOverviewUploadCompleted(false);
  }, [uploadError, logger]);

  const handleUploadPress = () => {
    logger.logGatedMatchOverviewUploadClick();
    void triggerUpload();
  };

  return (
    <Box sx={styles.container}>
      {/* Relative wrapper — the absolute CTA card positions against this box */}
      <Box sx={styles.listWrapper}>
        <Heading level={4} sx={styles.heading} testID="vj-gated-match-overview-heading">
          {gt.gettext("Match overview")}
        </Heading>
        {/* greyedListContainer scopes ctaPositioner to the list area only,
                        so the heading stays unobstructed by the CTA card.
                        zIndex:3 keeps it above the dimOverlay (zIndex:2). */}
        <Box sx={styles.greyedListContainer}>
          {/* Greyed non-interactive placeholder list — reuses MatchQualification
                            for visual parity. Hidden from the a11y tree and blocked from touch.
                            View (not Box) used here so opacity applies reliably on web. Labels are
                            hardcoded rather than translated since this content is always blurred
                            and hidden behind the CTA card. */}
          <View
            accessibilityElementsHidden={true}
            importantForAccessibility="no-hide-descendants"
            pointerEvents="none"
            style={styles.greyedList}
          >
            <Heading level={isDesktop ? 3 : 2} sx={styles.subHeading}>
              Qualifications: 0 of 10
            </Heading>
            {PLACEHOLDER_ROWS.map(({ label, required }) => (
              <MatchQualification
                key={label}
                variant={MatchQualificationVariant.Missing}
                primaryText={label}
                required={required}
                onConfirm={noop}
                onReject={noop}
              />
            ))}
            <Box sx={styles.showMoreBox}>
              <Text level={3} sx={styles.showMoreText}>
                +5 more
              </Text>
            </Box>
          </View>

          {/* Centers the CTA card over the list rows */}
          <Box sx={[StyleSheet.absoluteFill, styles.ctaPositioner]} pointerEvents="box-none">
            <Box sx={styles.ctaCard} testID="vj-gated-match-overview-cta-card">
              <Box sx={styles.benefitRows}>
                <Heading level={isDesktop ? 4 : 3}>
                  {gt.gettext("You haven't added a resume yet")}
                </Heading>
                <BenefitRow label={gt.gettext("See how well this job fits you")} />
                <BenefitRow label={gt.gettext("Apply where you're likely to hear back")} />
                <BenefitRow label={gt.gettext("Match to the right roles, right away")} />
              </Box>

              {uploadError && (
                <Alert
                  status="critical"
                  title={uploadError.title}
                  explainerText={uploadError.message}
                  dismissAction={{ ariaLabel: gt.gettext("Dismiss"), onDismiss: clearUploadError }}
                />
              )}

              <Flex sx={styles.ctaButtonRow}>
                {isDesktop ? (
                  <>
                    <Button
                      variant="secondary"
                      onPress={handleUploadPress}
                      loading={uploading}
                      iconBefore={<Upload size="sm" color={colors.foregroundPrimary} />}
                    >
                      {gt.gettext("Upload your resume")}
                    </Button>
                    <Button variant="text" onPress={handleBuildPress}>
                      {gt.gettext("Build your resume")}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="text"
                    loading={uploading}
                    onPress={() => setIsAddResumeSheetVisible(true)}
                  >
                    {gt.gettext("Add your resume")}
                  </Button>
                )}
              </Flex>
            </Box>
          </Box>
        </Box>

        {/* 10% black dim — covers the full container including the heading */}
        <Box sx={[StyleSheet.absoluteFill, styles.dimOverlay]} pointerEvents="none" />
      </Box>

      {isAddResumeSheetVisible && (
        <AddResumeBottomSheet
          onClose={() => setIsAddResumeSheetVisible(false)}
          onBuildPress={handleBuildPress}
          onUploadPress={handleUploadPress}
        />
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing.s4,
    // Bleed past the horizontal padding ViewJobMainContent applies to job content
    // (CONTENT_MARGIN, 16) so the dimmed container spans the full screen width. The
    // listWrapper padding below re-indents the text to the original alignment.
    // Same negative-margin approach as JobComponent/LinkButton.
    marginHorizontal: -spacing.s4,
  },
  heading: {
    marginBlockEnd: spacing.s2,
  },
  listWrapper: {
    position: "relative",
    padding: spacing.s4,
  },
  greyedListContainer: {
    position: "relative",
    zIndex: 3,
  },
  greyedList: {
    opacity: 0.35,
    paddingBlockStart: spacing.s5,
    paddingBlockEnd: spacing.s5,
    // react-native-web doesn't support the RN 0.83 array form of `filter`,
    // so use the CSS string form — RN's native filter parser accepts it too.
    filter: "blur(3px)",
  },
  subHeading: {
    marginBlockEnd: spacing.s3,
  },
  showMoreBox: {
    marginTop: 4,
    flex: 1,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  showMoreText: {
    color: colors.foreground,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderStyle: "dotted",
    marginTop: 8,
  },
  dimOverlay: {
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    zIndex: 2,
  },
  ctaPositioner: {
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.s5,
  },
  ctaCard: {
    paddingVertical: spacing.s5,
    paddingHorizontal: spacing.s4,
    backgroundColor: colors.background,
    borderRadius: borderRadius.lg,
    shadowColor: colors.utilityShadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: spacing.s2,
    elevation: 4,
    alignItems: "center",
    gap: 12,
  },
  benefitRows: {
    gap: spacing.s2,
  },
  benefitRow: {
    flexDirection: "row",
    gap: spacing.sc2,
    alignItems: "center",
  },
  benefitIcon: {
    width: spacing.s5,
    height: spacing.s5,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    borderRadius: borderRadius.full,
  },
  ctaButtonRow: {
    paddingTop: spacing.s2,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.s2,
    flexWrap: "wrap",
  },
});
